import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  type TemplateItemCreateInput,
  useTemplateDetalheCompleto,
  useTemplateItemCreate,
  useTemplateItemDelete,
} from '../../hooks/useTemplates'
import { extractApiError } from '../../lib/extractApiError'
import styles from '../_shared/CrudPage.module.css'

const TIPO_RESPOSTA_LABEL: Record<string, string> = {
  sim_nao: 'Sim/Não',
  numerico: 'Numérico',
  texto: 'Texto',
}

export default function TemplateDetalhePage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const detalhe = useTemplateDetalheCompleto(id)
  const itemCreate = useTemplateItemCreate(id)
  const itemDelete = useTemplateItemDelete(id)
  const [aberto, setAberto] = useState(false)
  const [novoItem, setNovoItem] = useState<TemplateItemCreateInput>({
    ordem: 1,
    pergunta: '',
    tipo_resposta: 'sim_nao',
    obrigatorio: true,
    requer_evidencia: false,
    unidade: '',
    valor_min: null,
    valor_max: null,
    ajuda: '',
    depende_de: null,
    condicao_valor: '',
  })

  if (detalhe.isLoading) return <div className={styles.loading}>Carregando template...</div>
  if (detalhe.error || !detalhe.data) {
    return <div className={styles.empty}>Template não encontrado.</div>
  }
  const tpl = detalhe.data
  const itens = (tpl.itens ?? []).slice().sort((a, b) => a.ordem - b.ordem)
  const proximaOrdem = (itens[itens.length - 1]?.ordem ?? 0) + 1

  async function handleAddItem(e: React.FormEvent) {
    e.preventDefault()
    if (!novoItem.pergunta.trim()) {
      notifications.show({ color: 'red', message: 'Pergunta é obrigatória.' })
      return
    }
    try {
      // Validação: se depende_de, exige condicao_valor
      if (novoItem.depende_de && !novoItem.condicao_valor) {
        notifications.show({
          color: 'red',
          message: 'Escolha a condição (Sim/Não) ao definir item pai.',
        })
        return
      }
      await itemCreate.mutateAsync({
        ...novoItem,
        ordem: novoItem.ordem || proximaOrdem,
        valor_min: novoItem.valor_min || null,
        valor_max: novoItem.valor_max || null,
        unidade: novoItem.unidade || '',
        depende_de: novoItem.depende_de || null,
        condicao_valor: novoItem.depende_de ? novoItem.condicao_valor || '' : '',
      })
      notifications.show({ color: 'green', message: 'Item adicionado.' })
      setNovoItem({
        ordem: proximaOrdem + 1,
        pergunta: '',
        tipo_resposta: novoItem.tipo_resposta,
        obrigatorio: true,
        requer_evidencia: false,
        unidade: '',
        valor_min: null,
        valor_max: null,
        ajuda: '',
      })
      setAberto(false)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar item.'),
      })
    }
  }

  async function handleDeleteItem(itemId: string) {
    if (!confirm('Remover este item do template? (irreversível se já houver execuções)')) return
    try {
      await itemDelete.mutateAsync(itemId)
      notifications.show({ color: 'green', message: 'Item removido.' })
    } catch {
      notifications.show({ color: 'red', message: 'Falha ao remover item.' })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            type="button"
            onClick={() => navigate('/checklists/templates')}
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>
            {tpl.nome} <span style={{ fontWeight: 400 }}>v{tpl.versao}</span>
          </h1>
          <p style={{ margin: '0.25rem 0 0', color: '#666' }}>
            {tpl.tipo_os_display ?? tpl.tipo_os}
            {tpl.tipo_equipamento &&
              ` • ${tpl.tipo_equipamento_display ?? tpl.tipo_equipamento}`}
          </p>
        </div>
      </header>

      {tpl.descricao && (
        <section
          style={{
            background: 'white',
            padding: '1.25rem',
            borderRadius: 8,
            marginBottom: '1rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Descrição</h2>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tpl.descricao}</p>
        </section>
      )}

      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Itens ({itens.length})</h2>
          <button
            type="button"
            data-testid="template-add-item-toggle"
            onClick={() => {
              setNovoItem((v) => ({ ...v, ordem: proximaOrdem }))
              setAberto((v) => !v)
            }}
            style={{
              padding: '0.4rem 0.8rem',
              background: aberto ? '#888' : '#1565C0',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            {aberto ? '× Cancelar' : '+ Novo item'}
          </button>
        </div>

        {aberto && (
          <form
            onSubmit={handleAddItem}
            style={{
              display: 'grid',
              gridTemplateColumns: '4ch 2fr 1fr 1fr 1fr',
              gap: '0.5rem',
              padding: '0.75rem',
              background: '#FAFAFA',
              borderRadius: 4,
              marginBottom: '1rem',
              alignItems: 'end',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
              #
              <input
                type="number"
                data-testid="item-ordem"
                value={novoItem.ordem}
                onChange={(e) =>
                  setNovoItem({ ...novoItem, ordem: parseInt(e.target.value, 10) || 1 })
                }
                style={{ padding: '0.4rem' }}
                min={1}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
              Pergunta *
              <input
                data-testid="item-pergunta"
                value={novoItem.pergunta}
                onChange={(e) => setNovoItem({ ...novoItem, pergunta: e.target.value })}
                required
                placeholder="ex.: Filtros estão limpos?"
                style={{ padding: '0.4rem' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
              Tipo
              <select
                data-testid="item-tipo"
                value={novoItem.tipo_resposta}
                onChange={(e) =>
                  setNovoItem({
                    ...novoItem,
                    tipo_resposta: e.target.value as 'sim_nao' | 'numerico' | 'texto',
                  })
                }
                style={{ padding: '0.4rem' }}
              >
                <option value="sim_nao">Sim/Não</option>
                <option value="numerico">Numérico</option>
                <option value="texto">Texto</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
              Unidade
              <input
                data-testid="item-unidade"
                value={novoItem.unidade ?? ''}
                onChange={(e) => setNovoItem({ ...novoItem, unidade: e.target.value })}
                placeholder={novoItem.tipo_resposta === 'numerico' ? 'ex: °C, Pa' : 'n/a'}
                disabled={novoItem.tipo_resposta !== 'numerico'}
                style={{ padding: '0.4rem' }}
              />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{ display: 'flex', gap: '0.25rem', fontSize: '0.75rem' }}>
                <input
                  type="checkbox"
                  data-testid="item-obrigatorio"
                  checked={novoItem.obrigatorio ?? true}
                  onChange={(e) => setNovoItem({ ...novoItem, obrigatorio: e.target.checked })}
                />
                Obrigatório
              </label>
              <label style={{ display: 'flex', gap: '0.25rem', fontSize: '0.75rem' }}>
                <input
                  type="checkbox"
                  data-testid="item-evidencia"
                  checked={novoItem.requer_evidencia ?? false}
                  onChange={(e) =>
                    setNovoItem({ ...novoItem, requer_evidencia: e.target.checked })
                  }
                />
                📷 Evidência
              </label>
              <button
                type="submit"
                disabled={itemCreate.isPending}
                data-testid="item-submit"
                style={{
                  padding: '0.3rem 0.5rem',
                  background: '#2E7D32',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {itemCreate.isPending ? '...' : 'Adicionar'}
              </button>
            </div>
          </form>
        )}

        {/* Sub-form opcional pra lógica condicional. Exibido só se item dependente. */}
        {aberto && itens.filter((it) => it.tipo_resposta === 'sim_nao').length > 0 && (
          <form
            onSubmit={(e) => e.preventDefault()}
            style={{
              padding: '0.75rem',
              background: '#FFF8E1',
              borderRadius: 4,
              marginBottom: '1rem',
              fontSize: '0.85rem',
            }}
          >
            <strong>Lógica condicional (opcional):</strong>
            <p style={{ margin: '0.25rem 0', fontSize: '0.8rem', color: '#666' }}>
              Este item só aparece quando o item pai for respondido com a condição abaixo.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', flex: 2, fontSize: '0.75rem' }}>
                Item pai (sim/não)
                <select
                  data-testid="item-depende-de"
                  value={novoItem.depende_de ?? ''}
                  onChange={(e) =>
                    setNovoItem({
                      ...novoItem,
                      depende_de: e.target.value || null,
                      // Se desligou depende, zera condicao_valor
                      condicao_valor: e.target.value ? novoItem.condicao_valor : '',
                    })
                  }
                  style={{ padding: '0.4rem' }}
                >
                  <option value="">— sem dependência —</option>
                  {itens
                    .filter((it) => it.tipo_resposta === 'sim_nao')
                    .map((it) => (
                      <option key={it.id} value={it.id}>
                        #{it.ordem} — {it.pergunta.slice(0, 50)}
                      </option>
                    ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', flex: 1, fontSize: '0.75rem' }}>
                Condição
                <select
                  data-testid="item-condicao-valor"
                  value={novoItem.condicao_valor ?? ''}
                  onChange={(e) => setNovoItem({ ...novoItem, condicao_valor: e.target.value })}
                  disabled={!novoItem.depende_de}
                  style={{ padding: '0.4rem' }}
                >
                  <option value="">— escolher —</option>
                  <option value="sim">Resposta = Sim</option>
                  <option value="nao">Resposta = Não</option>
                </select>
              </label>
            </div>
          </form>
        )}

        {itens.length === 0 ? (
          <p style={{ color: '#666', margin: 0 }}>Nenhum item cadastrado neste template.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#F5F5F5' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', width: '4ch' }}>#</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Pergunta</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Tipo</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Limites</th>
                <th style={{ padding: '0.5rem' }}>Obrig.</th>
                <th style={{ padding: '0.5rem' }}>Foto</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Depende de</th>
                <th style={{ padding: '0.5rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item) => (
                <tr key={item.id} style={{ borderTop: '1px solid #EEE' }}>
                  <td style={{ padding: '0.5rem' }}>{item.ordem}</td>
                  <td style={{ padding: '0.5rem' }}>
                    {item.pergunta}
                    {item.ajuda && (
                      <p
                        style={{
                          margin: '0.25rem 0 0',
                          fontSize: '0.75rem',
                          color: '#666',
                          fontStyle: 'italic',
                        }}
                      >
                        {item.ajuda}
                      </p>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {TIPO_RESPOSTA_LABEL[item.tipo_resposta] ?? item.tipo_resposta}
                    {item.unidade && ` (${item.unidade})`}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    {item.valor_min || item.valor_max
                      ? `${item.valor_min ?? '?'} – ${item.valor_max ?? '?'}`
                      : '—'}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                    {item.obrigatorio ? '✓' : ''}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                    {item.requer_evidencia ? '📷' : ''}
                  </td>
                  <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
                    {item.depende_de
                      ? `#${item.depende_de_pergunta ?? ''} = ${item.condicao_valor ?? ''}`
                      : '—'}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(item.id)}
                      disabled={itemDelete.isPending}
                      style={{
                        padding: '0.2rem 0.5rem',
                        background: 'white',
                        color: '#C62828',
                        border: '1px solid #C62828',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                      }}
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
